import { useEffect, useMemo, useState } from "react";
import {
  Button,
  Box,
  Drawer,
  IconButton,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { GridColDef, GridPaginationModel } from "@mui/x-data-grid";
import { frFR } from "@mui/x-data-grid/locales/frFR";
import StyledDataGrid from "./DataGridComponent";
import { getCoproPortfolio, exportCoproPortfolioCsv } from "../address/data4homeAPI";
import {
  COPRO_PORTFOLIO_PAGE_SIZE,
  CoproPortfolio,
  CoproPortfolioMandatFilter,
  CoproPortfolioMapPoint,
  asPortfolioText,
  formatPortfolioDate,
  formatPortfolioLots,
  formatSiren,
  formatSiret,
  normalizeCoproPortfolio,
  parsePortfolioCoord,
} from "./coproPortfolio";

export interface CoproPortfolioDrawerRequest {
  siret: string;
  portfolio: CoproPortfolio;
  /** Change à chaque ouverture pour revenir à la page embarquée. */
  resetKey: number;
}

interface PortfolioRow {
  id: string;
  nom: string;
  adresse: string;
  commune: string;
  lots: string;
  finMandat: string;
  siret: string;
  lat: number | null;
  long: number | null;
}

export interface CoproPortfolioMapState {
  points: CoproPortfolioMapPoint[];
  selectedId: string | null;
}

interface CoproPortfolioDrawerProps {
  open: boolean;
  request: CoproPortfolioDrawerRequest | null;
  onClose: () => void;
  onMapStateChange: (state: CoproPortfolioMapState) => void;
  onOpenAddress: (lat: number, lng: number) => void;
}

const COLUMNS: GridColDef[] = [
  { field: "nom", headerName: "Nom", flex: 1.4, minWidth: 140, sortable: false },
  { field: "adresse", headerName: "Adresse", flex: 1.6, minWidth: 160, sortable: false },
  { field: "commune", headerName: "Commune", flex: 1, minWidth: 110, sortable: false },
  { field: "lots", headerName: "Lots", flex: 0.5, minWidth: 70, sortable: false },
  { field: "finMandat", headerName: "Fin de mandat", flex: 0.9, minWidth: 120, sortable: false },
  { field: "siret", headerName: "SIRET", flex: 1.1, minWidth: 150, sortable: false },
];

function rowsFromPortfolio(portfolio: CoproPortfolio | null): PortfolioRow[] {
  return (portfolio?.copros ?? []).map((copro, index) => {
    const immat = asPortfolioText(copro.immatriculation);
    return {
      id: immat || `portfolio-row-${index}`,
      nom: asPortfolioText(copro.nomUsage),
      adresse: asPortfolioText(copro.adresse),
      commune: asPortfolioText(copro.commune),
      lots: formatPortfolioLots(copro.lots),
      finMandat: formatPortfolioDate(copro.dateFinMandat),
      siret: formatSiret(copro.siret),
      lat: parsePortfolioCoord(copro.lat),
      long: parsePortfolioCoord(copro.long),
    };
  });
}

export default function CoproPortfolioDrawer({
  open,
  request,
  onClose,
  onMapStateChange,
}: CoproPortfolioDrawerProps) {
  const [seenKey, setSeenKey] = useState<number | null>(null);
  const [mandat, setMandat] = useState<CoproPortfolioMandatFilter>("en_cours");
  const [pageIndex, setPageIndex] = useState(0);
  const [result, setResult] = useState<CoproPortfolio | null>(request?.portfolio ?? null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [exporting, setExporting] = useState(false);
  const [selectedId, setSelectedId] = useState<string | null>(null);

  if (request && request.resetKey !== seenKey) {
    setSeenKey(request.resetKey);
    setMandat("en_cours");
    setPageIndex(0);
    setResult(request.portfolio);
    setSelectedId(null);
    setError(null);
    setExporting(false);
    setLoading(false);
  }

  useEffect(() => {
    if (!open || !request) return;
    if (mandat === "en_cours" && pageIndex === 0) {
      setResult(request.portfolio);
      setLoading(false);
      setError(null);
      return;
    }

    let cancelled = false;
    setLoading(true);
    setError(null);
    // Grille 0-based : l'index 0 est la page embarquée. L'API est 1-based (page 2 = deuxième page).
    const apiPage = pageIndex + 1;
    getCoproPortfolio({
      siret: request.siret,
      page: apiPage,
      limit: COPRO_PORTFOLIO_PAGE_SIZE,
      mandat,
    })
      .then((data) => {
        if (cancelled) return;
        const portfolio = normalizeCoproPortfolio(data);
        if (!portfolio) {
          setError("Le portefeuille n'a pas pu être chargé.");
          setResult((current) => ({ ...(current ?? request.portfolio), copros: [] }));
          return;
        }
        setResult(portfolio);
      })
      .catch(() => {
        if (cancelled) return;
        setError("Le portefeuille n'a pas pu être chargé.");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [open, request, mandat, pageIndex]);

  const rows = useMemo(() => rowsFromPortfolio(open ? result : null), [open, result]);

  const selectedRow = rows.find((row) => row.id === selectedId) ?? null;
  const selectedPoint =
    selectedRow && selectedRow.lat != null && selectedRow.long != null
      ? { id: selectedRow.id, lat: selectedRow.lat, long: selectedRow.long }
      : null;

  useEffect(() => {
    if (!open) {
      onMapStateChange({ points: [], selectedId: null });
      return;
    }
    onMapStateChange({
      points: rows.flatMap((row) =>
        row.lat != null && row.long != null ? [{ id: row.id, lat: row.lat, long: row.long }] : []
      ),
      selectedId: selectedPoint?.id ?? null,
    });
  }, [open, rows, selectedPoint?.id, onMapStateChange]);

  const totals = result?.totals ?? request?.portfolio.totals;
  const totalForFilter = mandat === "en_cours" ? totals?.mandatEnCours : totals?.total;
  const rowCount = typeof totalForFilter === "number" && Number.isFinite(totalForFilter)
    ? totalForFilter
    : rows.length;

  const denomination = asPortfolioText(request?.portfolio.societe?.denomination);
  const siren = formatSiren(request?.portfolio.societe?.siren);
  const title = denomination || "Portefeuille";

  const handleMandatChange = (_event: React.MouseEvent<HTMLElement>, value: CoproPortfolioMandatFilter | null) => {
    if (!value || value === mandat) return;
    setMandat(value);
    setPageIndex(0);
    setSelectedId(null);
  };

  const handlePaginationChange = (model: GridPaginationModel) => {
    if (model.page === pageIndex) return;
    setPageIndex(model.page);
    setSelectedId(null);
  };

  // const handleOpenAddress = () => {
  //   if (!selectedPoint) return;
  //   onOpenAddress(selectedPoint.lat, selectedPoint.long);
  // };

  const handleExportCsv = async () => {
    if (!request || exporting) return;
    setExporting(true);
    setError(null);
    try {
      const response = await exportCoproPortfolioCsv(request.siret);
      const blob = response.data instanceof Blob
        ? response.data
        : new Blob([response.data], { type: "text/csv;charset=utf-8" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      const header = String(response.headers?.["content-disposition"] ?? "");
      const match = /filename="([^"]+)"/.exec(header);
      link.href = url;
      link.download = match?.[1] || `portefeuille-${request.siret.slice(0, 9)}.csv`;
      link.click();
      window.setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch {
      setError("L'export CSV n'a pas pu être généré.");
    } finally {
      setExporting(false);
    }
  };

  return (
    <Drawer
      anchor="bottom"
      open={open}
      onClose={onClose}
      variant="temporary"
      hideBackdrop
      ModalProps={{
        disablePortal: true,
        disableScrollLock: true,
        disableEnforceFocus: true,
        disableAutoFocus: true,
        style: { position: "absolute" },
      }}
      sx={{
        position: "absolute",
        pointerEvents: "none",
        "& .MuiDrawer-paper": {
          pointerEvents: "auto",
          position: "absolute",
          left: 0,
          right: 0,
          bottom: 0,
          height: "min(520px, 68%)",
          width: "100%",
          maxWidth: "100%",
          borderTopLeftRadius: 12,
          borderTopRightRadius: 12,
          boxSizing: "border-box",
        },
      }}
    >
      <Box
        sx={{ display: "flex", flexDirection: "column", height: "100%", minHeight: 0, p: 1.5, gap: 1 }}
        role="dialog"
        aria-label="Portefeuille du syndic"
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
          <Box sx={{ flex: "1 1 180px", minWidth: 0 }}>
            <Typography variant="subtitle1" noWrap sx={{ fontWeight: 700, lineHeight: 1.2 }}>
              {title}
            </Typography>
            {siren && (
              <Typography variant="caption" color="text.secondary">
                SIREN {siren}
              </Typography>
            )}
          </Box>
          <ToggleButtonGroup
            exclusive
            size="small"
            value={mandat}
            onChange={handleMandatChange}
            aria-label="Filtre des mandats"
          >
            <ToggleButton value="en_cours" sx={{ textTransform: "none", px: 1.25 }}>
              Mandats en cours
            </ToggleButton>
            <ToggleButton value="tous" sx={{ textTransform: "none", px: 1.25 }}>
              Tous
            </ToggleButton>
          </ToggleButtonGroup>
          {/* <Link
            component="button"
            type="button"
            variant="body2"
            underline={selectedPoint ? "always" : "none"}
            onClick={handleOpenAddress}
            aria-disabled={!selectedPoint}
            sx={{
              border: 0,
              background: "none",
              fontWeight: 600,
              whiteSpace: "nowrap",
              cursor: selectedPoint ? "pointer" : "default",
              color: selectedPoint ? "primary.main" : "text.disabled",
              pointerEvents: selectedPoint ? "auto" : "none",
            }}
          >
            Ouvrir cette adresse
          </Link> */}
          <Button
            size="small"
            onClick={handleExportCsv}
            disabled={!request || exporting}
            sx={{ textTransform: "none", fontWeight: 600, whiteSpace: "nowrap" }}
          >
            {exporting ? "Export…" : "Export CSV"}
          </Button>
          <IconButton onClick={onClose} aria-label="Fermer le portefeuille" size="small">
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
        {error && (
          <Typography variant="body2" color="error">
            {error}
          </Typography>
        )}
        <Box sx={{ flex: 1, minHeight: 0 }}>
          <StyledDataGrid
            rows={rows}
            columns={COLUMNS}
            loading={loading}
            paginationMode="server"
            rowCount={rowCount}
            paginationModel={{ page: pageIndex, pageSize: COPRO_PORTFOLIO_PAGE_SIZE }}
            onPaginationModelChange={handlePaginationChange}
            pageSizeOptions={[COPRO_PORTFOLIO_PAGE_SIZE]}
            hideFooter={false}
            hideFooterPagination={false}
            disableRowSelectionOnClick={false}
            rowSelectionModel={selectedId ? [selectedId] : []}
            onRowClick={(params) => setSelectedId(String(params.id))}
            textNorows="Aucune copropriété dans ce portefeuille"
            dataGridContainerStyle={{ height: "100%" }}
            localeText={{
              ...frFR.components.MuiDataGrid.defaultProps.localeText,
              noRowsLabel: "Aucune copropriété dans ce portefeuille",
            }}
            sx={{
              height: "100%",
              "& .MuiTablePagination-selectLabel, & .MuiTablePagination-input": {
                display: "none",
              },
              // La grille partagée peint le survol de la même couleur que la ligne.
              // `&&` passe devant ces règles : violet au survol, violet plus foncé
              // et liseré bleu pour la ligne sélectionnée.
              "&& .MuiDataGrid-row": {
                cursor: "pointer",
              },
              "&& .MuiDataGrid-row:nth-child(n):hover, && .MuiDataGrid-row.Mui-hovered": {
                backgroundColor: "#ddd6fc",
              },
              "&& .MuiDataGrid-row.Mui-selected": {
                backgroundColor: "#c4b5f8",
                boxShadow: "inset 4px 0 0 #124376",
              },
              "&& .MuiDataGrid-row.Mui-selected:hover, && .MuiDataGrid-row.Mui-selected.Mui-hovered": {
                backgroundColor: "#b6a4f4",
                boxShadow: "inset 4px 0 0 #124376",
              },
            }}
          />
        </Box>
      </Box>
    </Drawer>
  );
}
